import type { Dispatch, PropsWithChildren, ReactElement } from 'react';
import { useReducer, createContext, useContext, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import type {
  ParticipantDetailsAction,
  ParticipantDetailsState,
} from './participantDetailsReducer';
import participantDetailsReducer from './participantDetailsReducer';
import { useApp } from '../../../../../hooks/AppProvider';
import GeneralError from '../../../../../views/error/GeneralError';
import {
  GET_RESULT_DETAIL,
  type GetResultDetailData,
  type GetResultDetailVariables,
} from './graphql/getResultDetail';
import type { DetailUrlParams } from '../../../../../views/types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Empty from '@fastnorth/kraken/components/empty';
import type { ResultEvent } from '../../../../../types/events';
import logDebugMessage from '../../../../../utils/logDebugMessage';
import { DEFAULT_FINISHER_CERTIFICATE, DEFAULT_RESULT_DETAILS } from './mocks';
import useAppLocation from '../../../../../hooks/useAppLocation';
import {
  GET_EVENT_TEMPLATE_CONTENT_BY_ID,
  GET_FINISHER_CERTIFICATE_BY_ID,
  type GetEventTemplateContentByIdData,
  type GetEventTemplateContentByIdVariables,
  type GetFinisherCertificateByIdData,
  type GetFinisherCertificateByIdVariables,
} from '../components/finisher-certificate/graphql/getFinisherCertificateById';
import ContextualLoadingSkeleton from '../../../../../hooks/components/ContextualLoadingSkeleton';

export type ParticipantDetailsProviderProps = {
  sampleMode?: boolean;
};

export const defaultState: ParticipantDetailsState = {
  photos: undefined,
  awards: undefined,
};

export type ParticipantDetailsProviderCtx = [
  ParticipantDetailsState,
  Dispatch<ParticipantDetailsAction>,
];

const ParticipantDetailsCtx = createContext<ParticipantDetailsProviderCtx>([
  defaultState,
  () => null,
]);

const ParticipantDetailsProvider = ({
  children,
  sampleMode = false,
}: PropsWithChildren<ParticipantDetailsProviderProps>): ReactElement => {
  const [state, dispatch] = useReducer(participantDetailsReducer, defaultState);
  const [{ event = {} as ResultEvent }] = useApp();
  const { uniqueCode } = event;
  const { resultUniqueCode, certificateId } = useParams<DetailUrlParams>();
  const { isCertificatePreview } = useAppLocation();
  const {
    t,
    i18n: { language: locale },
  } = useTranslation('translation', { keyPrefix: 'karwa.event.race.result' });

  const [loadDetails, { data, loading, error, called }] = useLazyQuery<
    GetResultDetailData,
    GetResultDetailVariables
  >(GET_RESULT_DETAIL, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [loadEventTemplateContent, { data: certData, error: certError }] = useLazyQuery<
    GetEventTemplateContentByIdData,
    GetEventTemplateContentByIdVariables
  >(GET_EVENT_TEMPLATE_CONTENT_BY_ID, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [loadLegacyCertificateById, { data: legacyCertData, error: legacyCertError }] =
    useLazyQuery<GetFinisherCertificateByIdData, GetFinisherCertificateByIdVariables>(
      GET_FINISHER_CERTIFICATE_BY_ID,
      {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
      }
    );

  useEffect(() => {
    if (data?.getResultDetail) {
      dispatch({ type: 'setResultDetail', payload: data.getResultDetail });
      return;
    }

    if (!sampleMode || !isCertificatePreview) {
      return;
    }

    const template = certData?.getEventTemplateContentById;
    const legacy = legacyCertData?.getCertificate;

    if (!template && !legacy) {
      return;
    }

    dispatch({
      type: 'setResultDetail',
      payload: {
        ...DEFAULT_RESULT_DETAILS,
        ...(template ? { finisherCertificateTemplate: template } : {}),
        ...(legacy ? { finisherCertificate: legacy } : {}),
      },
    });
  }, [
    certData?.getEventTemplateContentById,
    data?.getResultDetail,
    dispatch,
    isCertificatePreview,
    legacyCertData?.getCertificate,
    sampleMode,
  ]);

  useEffect(() => {
    if (sampleMode && isCertificatePreview) {
      if (uniqueCode) {
        if (certificateId) {
          loadEventTemplateContent({
            variables: {
              eventUniqueCode: uniqueCode,
              id: certificateId,
            },
          }).then(({ data: certificateData }) => {
            // if the template is not found, try to load the legacy certificate by id
            if (!certificateData?.getEventTemplateContentById) {
              loadLegacyCertificateById({
                variables: {
                  eventUniqueCode: uniqueCode,
                  certificateId,
                },
              });
            }
          });
        } else {
          // we need to set the default certificate
          dispatch({
            type: 'setResultDetail',
            payload: {
              ...DEFAULT_RESULT_DETAILS,
              finisherCertificate: DEFAULT_FINISHER_CERTIFICATE,
            },
          });
        }
      }
    } else if (uniqueCode && resultUniqueCode) {
      loadDetails({
        variables: {
          locale,
          eventUniqueCode: uniqueCode,
          resultUniqueCode: resultUniqueCode,
        },
      });
    }
  }, [
    certificateId,
    isCertificatePreview,
    loadEventTemplateContent,
    loadLegacyCertificateById,
    loadDetails,
    locale,
    resultUniqueCode,
    sampleMode,
    uniqueCode,
  ]);

  if (sampleMode && isCertificatePreview) {
    return (
      <ParticipantDetailsCtx.Provider value={[state, dispatch]}>
        {children}
      </ParticipantDetailsCtx.Provider>
    );
  }

  if (error || certError || legacyCertError) {
    logDebugMessage('[ParticipantDetailsProvider] rendering error');
    return <GeneralError id={'participant-details-error'} error={error} />;
  }

  if (!called || (loading && !data)) {
    logDebugMessage('[ParticipantDetailsProvider] rendering skeleton');
    return <ContextualLoadingSkeleton />;
  }

  if (!loading && called && (!data || !data.getResultDetail)) {
    logDebugMessage('[ParticipantDetailsProvider] rendering empty state');
    return (
      <Empty
        heading={t('empty_state.heading', { resultUniqueCode })}
        description={t('empty_state.description')}
        image={{
          src: 'https://cdn.raceroster.com/assets/cerbicons/empty-race-results.svg',
          alt: t('empty_state.image_alt_text'),
          variant: 'illustration',
        }}
      />
    );
  }

  logDebugMessage('[ParticipantDetailsProvider] rendering children');
  return (
    <ParticipantDetailsCtx.Provider value={[state, dispatch]}>
      {children}
    </ParticipantDetailsCtx.Provider>
  );
};

export const useParticipantDetails = (): ParticipantDetailsProviderCtx =>
  useContext<ParticipantDetailsProviderCtx>(ParticipantDetailsCtx);

export default ParticipantDetailsProvider;
